import { Collapse, Grid } from '@mui/material'
import { KDatePicker } from 'components/core/KDatePicker'
import { useCurrentStore, useOrdering } from 'hooks'
import { useTranslation } from 'react-i18next'
import {
  canSetOrderDate,
  getTotalDelayTimeInMinutes,
  calculateAvailableTimeSlots,
  getStartOfDay,
  calculateEarliestAvailableTime,
} from 'services'
import { SubmitOrderSectionProps } from './SubmitOrderSectionProps'
import { useCallback, useEffect, useState } from 'react'
import { KSelect } from 'components/core/KSelect'
import { isEmpty } from 'lodash'
import { timestampFormat } from 'models'
import moment from 'moment'

interface OrderInfoTimeInputState {
  timeSlots: moment.Moment[]
  canSetDate: boolean
  selectTimeLabel: string
  selectDateLabel: string
  disableDatePicker: boolean
}

const orderTypeLabels: { [key: string]: string } = {
  delivery: 'Delivery',
  pickup: 'Pickup',
  dinein: 'Dine-in',
}

export const OrderInfoTimeInput = ({ form }: SubmitOrderSectionProps) => {
  const formFieldName = 'orderInfo.time'
  const { t } = useTranslation()
  const {
    register,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = form
  const { store, currentTime, parseTime, formatTime, timezone, isOpening } =
    useCurrentStore()
  const { order, saveOrderProps } = useOrdering()

  const isASAP = order?.is_asap
  const orderType = order?.order_type

  const [date, setDate] = useState(
    order?.timeIsoFormat ? parseTime(order?.timeIsoFormat) : currentTime(),
  )

  const [state, setState] = useState<OrderInfoTimeInputState>({
    timeSlots: [],
    canSetDate: false,
    selectTimeLabel: '',
    selectDateLabel: '',
    disableDatePicker: false,
  })

  const clearOrderTime = useCallback(() => {
    saveOrderProps({
      time: undefined,
      timeIsoFormat: undefined,
    })
    setValue(formFieldName, undefined)
  }, [saveOrderProps, setValue, formFieldName])

  const setOrderTime = useCallback(
    (timeIsoString: string) => {
      const selectedTime = parseTime(timeIsoString)

      if (
        selectedTime &&
        selectedTime.isValid() &&
        !selectedTime.isSame(date, 'day')
      ) {
        setDate(selectedTime.clone())
      }

      saveOrderProps({
        time: formatTime(timeIsoString, timestampFormat),
        timeIsoFormat: timeIsoString,
      })
      setValue(formFieldName, timeIsoString)
      clearErrors(formFieldName)
    },
    [
      saveOrderProps,
      formatTime,
      setValue,
      clearErrors,
      formFieldName,
      parseTime,
      date,
    ],
  )

  useEffect(() => {
    if (!store || !orderType) return

    if (isASAP === true) {
      // Clear order time selection selecting ASAP
      if (order?.timeIsoFormat) {
        clearOrderTime()
      }
      clearErrors(formFieldName)
      return
    }

    // Select Later business logic
    const orderTypeLabel = orderTypeLabels[orderType]
    const selectTimeLabel = t(`${orderTypeLabel} time`)
    const selectDateLabel = t(`${orderTypeLabel} date`)

    let canSetDate = canSetOrderDate(store, orderType) ?? false
    let disableDatePicker = false

    // Check if store is currently open
    const isStoreOpen = isOpening === true

    // If store is closed, calculate the earliest available time
    let baseTime = currentTime()
    if (!isStoreOpen) {
      const earliestTime = calculateEarliestAvailableTime(store)
      if (earliestTime) {
        baseTime = earliestTime
        if (
          !baseTime.isSame(date, 'day') &&
          date.isSame(currentTime(), 'day')
        ) {
          setDate(baseTime)
        }
        if (!canSetDate) {
          canSetDate = true
          disableDatePicker = !order?.timeIsoFormat
        }
      }
    }

    const delayTime = getTotalDelayTimeInMinutes(store, orderType)

    const day = getStartOfDay(date, timezone)

    const useSelectedDateForCalculation =
      canSetDate && (!disableDatePicker || order?.timeIsoFormat)
    const calculationBaseTime = useSelectedDateForCalculation ? day : baseTime

    const timeSlots =
      calculateAvailableTimeSlots(
        calculationBaseTime,
        delayTime,
        store.biz_hours,
        timezone,
      ) ?? []

    setState({
      timeSlots,
      canSetDate,
      selectTimeLabel,
      selectDateLabel,
      disableDatePicker,
    })

    if (timeSlots.length === 0) {
      // No time slots available
      const message = canSetDate
        ? t(
            'No available time for selected date. Change to another date or different type of order.',
          )
        : t('No available time today. Change to another type of order.')
      setError(formFieldName, { message })

      // Clear time if selected before
      if (order?.timeIsoFormat) {
        clearOrderTime()
      }
    } else {
      // Having time slots, need to make sure the selected time is still available.
      // If not then clear the selected time.
      if (order?.timeIsoFormat) {
        const selectedTime = moment(order?.timeIsoFormat)
        if (timeSlots.every((e) => !e.isSame(selectedTime))) {
          setOrderTime(timeSlots[0].format())
        }
      } else {
        setOrderTime(timeSlots[0].format())
      }
      clearErrors(formFieldName)
    }
  }, [orderType, isASAP, date, isOpening]) // eslint-disable-line react-hooks/exhaustive-deps

  // Enable the date picker if a future time (not today) is selected.
  // This ensures users can change the date when their selected time is not for the current day.
  useEffect(() => {
    if (order?.timeIsoFormat && state.canSetDate && state.disableDatePicker) {
      const selectedTime = parseTime(order.timeIsoFormat)

      if (
        selectedTime &&
        selectedTime.isValid() &&
        !selectedTime.isSame(currentTime(), 'day')
      ) {
        setState((prevState) => ({
          ...prevState,
          disableDatePicker: false,
        }))
      }
    }
  }, [
    order?.timeIsoFormat,
    state.canSetDate,
    state.disableDatePicker,
    parseTime,
    currentTime,
  ])

  return (
    <Collapse in={isASAP !== true}>
      <Grid
        container
        spacing={2.5}
        sx={{ flexDirection: state.canSetDate ? 'row-reverse' : 'row' }}
      >
        <Grid item xs={12} sm={6}>
          <KSelect
            label={state.selectTimeLabel}
            helperText={errors?.orderInfo?.time?.message}
            error={!!errors?.orderInfo?.time}
            required
            {...register(formFieldName, {
              validate: (value) => {
                if (isASAP !== true && isEmpty(value))
                  return t('{{label}} is required', {
                    label: state.selectTimeLabel,
                  })
              },
              onChange: (e) => {
                const value = e.target.value
                setValue(formFieldName, value)
                if (value) {
                  setOrderTime(value)
                } else {
                  clearOrderTime()
                }
              },
            })}
            options={state.timeSlots?.map((e) => ({
              key: e.clone().format('hh:mm A'),
              value: e.format(),
            }))}
            value={order?.timeIsoFormat}
          />
        </Grid>

        {state.canSetDate && (
          <Grid item xs={12} sm={6}>
            <KDatePicker
              required
              label={state.selectDateLabel}
              value={date}
              timezone={store?.timezone}
              disablePast
              maxDate={currentTime().add(30, 'days')}
              disabled={state.disableDatePicker}
              onChange={(value) => {
                if (value && !value.isSame(date, 'day')) {
                  if (order?.timeIsoFormat) {
                    clearOrderTime()
                  }
                  setDate(value)
                }
              }}
            />
          </Grid>
        )}
      </Grid>
    </Collapse>
  )
}
